// context/TokenContext.jsx - GraphQL Integrated Version
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useAuth } from './AuthContext';

// GraphQL Query to get user's token balance (from me query)
const GET_ME = gql`
  query GetMe {
    me {
      id
      tokenBalance
    }
  }
`;

const TokenContext = createContext({
  balance: 0,
  transactions: [],
  loading: false,
  error: null,
  pendingTokens: 0,
  // Methods
  updateBalance: () => {},
  addTransaction: () => {},
  distributeTokens: () => {},
  transferTokens: () => {},
  getTransactionHistory: () => {},
  refreshBalance: () => {},
  awardTokens: () => {}
});

export const useTokens = () => {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error('useTokens must be used within TokenProvider');
  }
  return context;
};

export const TokenProvider = ({ children }) => {
  const { user, isLoggedIn } = useAuth();
  
  const [transactions, setTransactions] = useState([]);
  const [pendingTokens, setPendingTokens] = useState(0);
  const [error, setError] = useState(null);

  // GraphQL Query for user data including token balance
  const { 
    data: userData, 
    loading: userLoading,
    refetch: refetchUser 
  } = useQuery(GET_ME, {
    skip: !isLoggedIn,
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000 // Poll every 30 seconds for balance updates
  });

  // Get balance from GraphQL or fallback
  const balance = userData?.me?.tokenBalance || 0;

  // Load transactions from localStorage (can be upgraded to GraphQL later)
  useEffect(() => {
    if (user) {
      try {
        const storedTransactions = localStorage.getItem(`token_transactions_${user.id}`) || '[]';
        setTransactions(JSON.parse(storedTransactions));
        
        const storedPending = localStorage.getItem(`pending_tokens_${user.id}`) || '0';
        setPendingTokens(parseInt(storedPending));
      } catch (err) {
        console.error('Error loading token data:', err);
      }
    }
  }, [user]);

  // Update balance (now just triggers refetch since balance comes from backend)
  const updateBalance = useCallback((newBalance) => {
    // Balance is now managed by backend, just refetch
    refetchUser();
  }, [refetchUser]);

  // Add transaction (local storage for now - could be GraphQL later)
  const addTransaction = useCallback((transaction) => {
    if (!user) return null;
    
    const newTransaction = {
      id: `tx_${Date.now()}`,
      userId: user.id,
      timestamp: new Date().toISOString(),
      ...transaction
    };
    
    setTransactions(prev => {
      const updated = [newTransaction, ...prev];
      localStorage.setItem(`token_transactions_${user.id}`, JSON.stringify(updated));
      return updated;
    });
    
    return newTransaction;
  }, [user]);

  // Distribute tokens (now handled by backend via job completion)
  const distributeTokens = useCallback(async (jobId, workerId, amount, jobDetails) => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    try {
      // Token distribution is now handled by backend when job is approved
      // This function just records the transaction locally for display
      
      const transaction = addTransaction({
        type: 'earn',
        category: 'job_completion',
        amount: amount,
        description: `Completed job: ${jobDetails?.title || 'Job'}`,
        jobId: jobId,
        status: 'completed'
      });
      
      // Refetch to get updated balance
      await refetchUser();
      
      return { success: true, transactionId: transaction?.id };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [user, addTransaction, refetchUser]);

  // Transfer tokens (keep local for now)
  const transferTokens = useCallback(async (recipientId, amount, reason) => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    try {
      if (amount > balance) {
        throw new Error('Insufficient token balance');
      }

      const transaction = addTransaction({
        type: 'send',
        category: 'transfer',
        amount: amount,
        description: reason || 'Token transfer',
        toUserId: recipientId,
        status: 'completed'
      });

      // In production, this would call a GraphQL mutation
      // For now, store pending for recipient
      const recipientPending = localStorage.getItem(`pending_tokens_${recipientId}`) || '0';
      localStorage.setItem(`pending_tokens_${recipientId}`, 
        (parseInt(recipientPending) + amount).toString()
      );

      return transaction;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [user, balance, addTransaction]);

  // Award tokens (for tours, etc.)
  const awardTokens = useCallback(async (userId, amount, reason) => {
    if (!user || user.id !== userId) {
      throw new Error('Can only award tokens to current user');
    }
    
    try {
      const transaction = addTransaction({
        type: 'earn',
        category: 'tour_reward',
        amount: amount,
        description: reason || 'Tour completion reward',
        fromUserId: 'platform',
        status: 'completed'
      });
      
      // Refetch to get updated balance from backend
      await refetchUser();
      
      return transaction;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [user, addTransaction, refetchUser]);

  // Get transaction history with filters
  const getTransactionHistory = useCallback((filters = {}) => {
    let filtered = [...transactions];
    
    if (filters.type) {
      filtered = filtered.filter(tx => tx.type === filters.type);
    }
    
    if (filters.category) {
      filtered = filtered.filter(tx => tx.category === filters.category);
    }
    
    if (filters.startDate) {
      filtered = filtered.filter(tx => 
        new Date(tx.timestamp) >= new Date(filters.startDate)
      );
    }
    
    if (filters.endDate) {
      filtered = filtered.filter(tx => 
        new Date(tx.timestamp) <= new Date(filters.endDate)
      );
    }
    
    return filtered;
  }, [transactions]);

  // Refresh balance from backend
  const refreshBalance = useCallback(() => {
    refetchUser();
  }, [refetchUser]);

  const value = {
    balance,
    transactions,
    loading: userLoading,
    error,
    pendingTokens,
    updateBalance,
    addTransaction,
    distributeTokens,
    transferTokens,
    getTransactionHistory,
    refreshBalance,
    awardTokens
  };

  return (
    <TokenContext.Provider value={value}>
      {children}
    </TokenContext.Provider>
  );
};